import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiTruck,
  FiUsers,
  FiCalendar,
  FiDollarSign,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiShield,
  FiFileText,
  FiPlus,
  FiTrash2,
  FiDownload,
  FiEye,
  FiUserX,
} from 'react-icons/fi';
import { FaCar } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    cars: { total: 0, available: 0, rented: 0, maintenance: 0 },
    clients: { total: 0, active: 0, blacklisted: 0 },
    reservations: { total: 0, active: 0, pending: 0 },
    revenue: { total: 0, monthly: 0, weekly: 0 },
    users: { total: 0, admin: 0, staff: 0, viewer: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    category: 'Laws',
    description: '',
    file: null
  });

  useEffect(() => {
    fetchDashboardData();
    console.log('Dashboard: Current user role:', user?.role); // Debug log
    if (user?.role === 'superadmin' || user?.role === 'admin') {
      console.log('Dashboard: Fetching documents for role:', user.role); // Debug log
      fetchDocuments();
    } else {
      console.log('Dashboard: User role not authorized for documents:', user?.role); // Debug log
    }
  }, [user?.role]);

  const fetchDashboardData = async () => {
    try {
      // Fetch car stats
      let carStats = { data: { data: {} } };
      try {
        const carResponse = await api.get('/api/cars/stats/overview');
        carStats = carResponse;
        console.log('✅ Cars API response:', carResponse.data);
      } catch (error) {
        console.error('❌ Error fetching car stats:', error);
      }

      // Fetch client stats
      let clientStats = { data: { data: {} } };
      try {
        const clientResponse = await api.get('/api/clients/stats/overview');
        clientStats = clientResponse;
        console.log('✅ Clients API response:', clientResponse.data);
      } catch (error) {
        console.error('❌ Error fetching client stats:', error);
      }

      // Fetch blacklist stats if admin
      let blacklistStats = { total: 0, recent: 0 };
      if (user?.role === 'admin' || user?.role === 'superadmin') {
        try {
          const blacklistResponse = await api.get('/api/blacklist/stats');
          blacklistStats = blacklistResponse.data.data;
          console.log('✅ Blacklist API response:', blacklistResponse.data);
        } catch (error) {
          console.error('❌ Error fetching blacklist stats:', error);
        }
      }

      // Fetch reservation stats
      let reservationStats = { data: { data: {} } };
      try {
        const reservationResponse = await api.get('/api/reservations/stats/overview');
        reservationStats = reservationResponse;
        console.log('✅ Reservations API response:', reservationResponse.data);
      } catch (error) {
        console.error('❌ Error fetching reservation stats:', error);
      }

      // Fetch revenue stats
      let revenueStats = { data: { data: {} } };
      try {
        const revenueResponse = await api.get('/api/payments/revenue/stats');
        revenueStats = revenueResponse;
        console.log('✅ Revenue API response:', revenueResponse.data);
      } catch (error) {
        console.error('❌ Error fetching revenue stats:', error);
        if (error.response?.status === 401) {
          console.error('   Authentication failed - check if user is logged in');
        } else if (error.response?.status === 403) {
          console.error('   Access denied - check user permissions');
        }
        // Keep default empty object to prevent crashes
      }

      // Fetch user stats if super admin
      let userStats = { total: 0, admin: 0, staff: 0, viewer: 0 };
      if (user?.role === 'superadmin') {
        try {
          const usersResponse = await api.get('/api/auth/users');
          const users = usersResponse.data.data;
          userStats = {
            total: users.length,
            admin: users.filter(u => u.role === 'admin').length,
            staff: users.filter(u => u.role === 'staff').length,
            viewer: users.filter(u => u.role === 'viewer').length,
          };
        } catch (error) {
          console.error('Error fetching user stats:', error);
        }
      }

      // Use real data from API calls
      const realStats = {
        cars: carStats.data.data,
        clients: { ...clientStats.data.data, blacklisted: blacklistStats.total },
        reservations: reservationStats.data.data,
        revenue: revenueStats.data.data,
        users: userStats,
        blacklist: blacklistStats,
      };

      console.log('📊 Final dashboard stats:', realStats);
      console.log('   - Cars:', realStats.cars);
      console.log('   - Clients:', realStats.clients);
      console.log('   - Reservations:', realStats.reservations);
      console.log('   - Revenue:', realStats.revenue);
      console.log('   - Users:', realStats.users);

      setStats(realStats);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/api/documents');
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Échec de la récupération des documents');
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();

    if (!documentForm.file) {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    const formData = new FormData();
    formData.append('title', documentForm.title);
    formData.append('category', documentForm.category);
    formData.append('description', documentForm.description);
    formData.append('file', documentForm.file);

    try {
      await api.post('/api/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Document ajouté avec succès');
      setShowAddDocumentModal(false);
      setDocumentForm({ title: '', category: 'Laws', description: '', file: null });
      fetchDocuments();
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error('Échec de l\'ajout du document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      try {
        await api.delete(`/api/documents/${documentId}`);
        toast.success('Document supprimé avec succès');
        fetchDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Échec de la suppression du document');
      }
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await api.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);

      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Échec du téléchargement');
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const response = await api.get(`/api/documents/${documentId}/view`, {
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        toast.error('Veuillez autoriser l\'ouverture des fenêtres pop-up');
        return;
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Échec de l\'ouverture du document');
    }
  };

  const carStatusData = [
    { name: 'Disponible', value: stats.cars.availableCars || 0, color: '#09A603' }, // Brand Green
    { name: 'Loué', value: stats.cars.rentedCars || 0, color: '#8C5CF2' }, // Brand Purple
    { name: 'Maintenance', value: stats.cars.maintenanceCars || 0, color: '#F59E0B' },
  ];

  // Transform real monthly revenue data from API
  const monthlyRevenueData = stats.revenue?.monthlyRevenue?.map(item => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return {
      month: monthNames[item._id - 1] || `Month ${item._id}`,
      revenue: item.revenue || 0
    };
  }) || [
      { month: 'Jan', revenue: 0 },
      { month: 'Feb', revenue: 0 },
      { month: 'Mar', revenue: 0 },
      { month: 'Apr', revenue: 0 },
      { month: 'May', revenue: 0 },
      { month: 'Jun', revenue: 0 },
    ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tableau de bord</h1>
        <div className="flex items-center gap-4">
          {user?.role === 'superadmin' && (
            <Link
              to="/users"
              className="btn bg-brand-green hover:bg-green-700 text-white flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <FiShield className="w-4 h-4" />
              Gérer les Agences
            </Link>
          )}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Dernière mise à jour : {new Date().toLocaleString('fr-FR')}
          </div>
        </div>
      </div>

      {/* SuperAdmin Simplified View */}
      {user?.role === 'superadmin' ? (
        <div className="space-y-6">
          {/* Agency Management Info */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 border-l-4 border-primary-600 rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-primary-900 dark:text-primary-100 mb-4 flex items-center">
              <span className="bg-primary-200 dark:bg-primary-800 p-2 rounded-lg mr-3">🏢</span>
              Gestion des Agences
            </h2>
            <p className="text-primary-800 dark:text-primary-200 mb-6 leading-relaxed">
              En tant que SuperAdmin, vous gérez la création et la configuration des agences de location de voitures.
              Chaque agence peut avoir ses propres utilisateurs, voitures et clients.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl font-extrabold text-primary-600 dark:text-primary-400 mb-1">
                  {stats.users.total}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agences Créées</div>
              </div>
              <div className="text-center bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl font-extrabold text-primary-600 dark:text-primary-400 mb-1">
                  {stats.cars.totalCars}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Voitures</div>
              </div>
              <div className="text-center bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl font-extrabold text-primary-600 dark:text-primary-400 mb-1">
                  {stats.clients.totalClients}
                </div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Clients</div>
              </div>
            </div>
          </div>

          {/* Essential Stats Cards - Only what SuperAdmin needs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card p-6 border-t-4 border-primary-500 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-primary-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-primary-100 dark:bg-primary-900/50 shadow-inner">
                  <FaCar className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Voitures</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.cars.totalCars}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-brand-green flex items-center"><span className="w-2 h-2 rounded-full bg-brand-green mr-2"></span>Disp: {stats.cars.availableCars}</span>
                  <span className="text-primary-600 flex items-center"><span className="w-2 h-2 rounded-full bg-primary-600 mr-2"></span>Loué: {stats.cars.rentedCars}</span>
                </div>
              </div>
            </div>

            <div className="card p-6 border-t-4 border-brand-green hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-green-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-green-100 dark:bg-green-900/50 shadow-inner">
                  <FiUsers className="h-7 w-7 text-brand-green dark:text-green-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Clients</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.clients.totalClients}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-brand-green flex items-center"><span className="w-2 h-2 rounded-full bg-brand-green mr-2"></span>Actif: {stats.clients.activeClients}</span>
                  <span className="text-red-500 flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>Blacklist: {stats.clients.blacklistedClients}</span>
                </div>
              </div>
            </div>

            <div className="card p-6 border-t-4 border-purple-500 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-purple-100 dark:bg-purple-900/50 shadow-inner">
                  <FiShield className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Utilisateurs</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.users.total}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-primary-600">Adm: {stats.users.admin}</span>
                  <span className="text-green-600">Stf: {stats.users.staff}</span>
                  <span className="text-gray-500">Vwr: {stats.users.viewer}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Blacklist Warning Card for Admin/SuperAdmin */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && stats.blacklist?.total > 0 && (
            <div className="card p-6 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900">
                  <FiUserX className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                    ⚠️ Attention: Clients Blacklistés
                  </h3>
                  <p className="text-red-700 dark:text-red-300">
                    Vous avez {stats.blacklist.total} client(s) dans votre liste noire.
                    {stats.blacklist.recent > 0 && ` ${stats.blacklist.recent} ajouté(s) ce mois.`}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to="/blacklist"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  <FiUserX className="w-4 h-4" />
                  Gérer la Liste Noire
                </Link>
              </div>
            </div>
          )}

          {/* Quick Actions for SuperAdmin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Actions Rapides
              </h3>
              <div className="space-y-3">
                <Link
                  to="/users"
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <FiShield className="h-5 w-5 text-primary-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Créer une Nouvelle Agence</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Ajouter un nouvel utilisateur admin</div>
                  </div>
                </Link>
                <Link
                  to="/documents"
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <FiFileText className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Gérer les Documents</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Lois, règles et contrats</div>
                  </div>
                </Link>
              </div>
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Informations Système
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Version:</span>
                  <span className="font-medium">1.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Environnement:</span>
                  <span className="font-medium">Production</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Base de données:</span>
                  <span className="font-medium">MongoDB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Statut:</span>
                  <span className="text-green-600 font-medium">● Opérationnel</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Full Dashboard for other roles */}
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-6 border-t-4 border-primary-500 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-primary-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-primary-100 dark:bg-primary-900/50 shadow-inner">
                  <FaCar className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Voitures</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.cars.totalCars}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-brand-green flex items-center"><span className="w-2 h-2 rounded-full bg-brand-green mr-2"></span>Disp: {stats.cars.availableCars}</span>
                  <span className="text-primary-600 flex items-center"><span className="w-2 h-2 rounded-full bg-primary-600 mr-2"></span>Loué: {stats.cars.rentedCars}</span>
                </div>
              </div>
            </div>

            <div className="card p-6 border-t-4 border-brand-green hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-green-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-green-100 dark:bg-green-900/50 shadow-inner">
                  <FiUsers className="h-7 w-7 text-brand-green dark:text-green-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Clients</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.clients.totalClients}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-brand-green flex items-center"><span className="w-2 h-2 rounded-full bg-brand-green mr-2"></span>Actif: {stats.clients.activeClients}</span>
                  <span className="text-red-500 flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>Blacklist: {stats.clients.blacklistedClients}</span>
                </div>
              </div>
            </div>

            <div className="card p-6 border-t-4 border-purple-500 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-purple-100 dark:bg-purple-900/50 shadow-inner">
                  <FiCalendar className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Locations</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.reservations.activeReservations || 0}</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-primary-600">Attente: {stats.reservations.pendingReservations || 0}</span>
                  <span className="text-gray-500">Total: {stats.reservations.totalReservations || 0}</span>
                </div>
              </div>
            </div>

            <div className="card p-6 border-t-4 border-yellow-500 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-yellow-50 rounded-full opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="flex items-center relative z-10">
                <div className="p-4 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 shadow-inner">
                  <FiDollarSign className="h-7 w-7 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-5">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Revenu Total</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{(stats.revenue?.totalRevenue || 0).toLocaleString()} MAD</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                <div className="flex items-center text-sm font-medium text-brand-green">
                  <FiTrendingUp className="h-4 w-4 mr-1" />
                  <span>Locations terminées</span>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Car Status Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Répartition des Statuts de Voitures</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={carStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {carStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Revenue Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenu Mensuel</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value.toLocaleString()} MAD`, 'Revenu']} />
                  <Bar dataKey="revenue" fill="#8C5CF2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* PDF Documents Section - Only for SuperAdmin and Admin */}
          {(user?.role === 'superadmin' || user?.role === 'admin') && (
            <div className="card p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Documents PDF</h3>
                {user?.role === 'superadmin' && (
                  <button
                    onClick={() => setShowAddDocumentModal(true)}
                    className="btn btn-primary flex items-center gap-2"
                    title="Ajouter un nouveau document PDF"
                  >
                    <FiPlus className="w-4 h-4" />
                    Ajouter un Document
                  </button>
                )}
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FiFileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun document PDF disponible</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Document
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Catégorie
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date d'Ajout
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {documents.map((doc) => (
                        <tr key={doc._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                                  <FiFileText className="h-5 w-5 text-red-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {doc.title}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {doc.filename}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {doc.category}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                              {doc.description || 'Aucune description'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewDocument(doc._id)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Voir le document"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(doc._id, doc.filename)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                title="Télécharger le document"
                              >
                                <FiDownload className="w-4 h-4" />
                              </button>
                              {user?.role === 'superadmin' && (
                                <button
                                  onClick={() => handleDeleteDocument(doc._id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  title="Supprimer le document"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}


          {/* Quick Actions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions Rapides</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/cars"
                className="flex items-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors border-l-4 border-primary-500 shadow-sm hover:shadow-md"
              >
                <FaCar className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-3" />
                <span className="font-medium text-primary-900 dark:text-primary-100">Gérer les Voitures</span>
              </Link>
              <Link
                to="/clients"
                className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors border-l-4 border-brand-green shadow-sm hover:shadow-md"
              >
                <FiUsers className="h-6 w-6 text-brand-green dark:text-green-400 mr-3" />
                <span className="font-medium text-green-900 dark:text-green-100">Gérer les Clients</span>
              </Link>
              <Link
                to="/reservations"
                className="flex items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors border-l-4 border-purple-500 shadow-sm hover:shadow-md"
              >
                <FiCalendar className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
                <span className="font-medium text-purple-900 dark:text-purple-100">Nouvelle Réservation</span>
              </Link>
              <Link
                to="/insurance"
                className="flex items-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors border-l-4 border-orange-500 shadow-sm hover:shadow-md"
              >
                <FiShield className="h-6 w-6 text-orange-600 dark:text-orange-400 mr-3" />
                <span className="font-medium text-orange-900 dark:text-orange-100">Suivi d'Assurance</span>
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Add Document Modal */}
      {showAddDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Ajouter un Nouveau Document PDF</h2>
            <form onSubmit={handleAddDocument} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Titre du Document *
                </label>
                <input
                  type="text"
                  required
                  value={documentForm.title}
                  onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                  className="input"
                  placeholder="Ex: Code de la Route, Contrat de Location..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Catégorie *
                </label>
                <select
                  required
                  value={documentForm.category}
                  onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                  className="input"
                >
                  <option value="Laws">Lois et Réglementations</option>
                  <option value="Rules">Règles et Procédures</option>
                  <option value="Contracts">Contrats et Formulaires</option>
                  <option value="Insurance">Assurance et Garanties</option>
                  <option value="Other">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={documentForm.description}
                  onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Description optionnelle du document..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fichier PDF *
                </label>
                <input
                  type="file"
                  required
                  accept=".pdf"
                  onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                  className="input"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Seuls les fichiers PDF sont acceptés. Taille maximale : 10 MB
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddDocumentModal(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Ajouter le Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 